import endOfMonth from 'date-fns/endOfMonth';
import endOfQuarter from 'date-fns/endOfQuarter';
import endOfWeek from 'date-fns/endOfWeek';
import endOfYear from 'date-fns/endOfYear';
import startOfMonth from 'date-fns/startOfMonth';
import startOfQuarter from 'date-fns/startOfQuarter';
import startOfWeek from 'date-fns/startOfWeek';
import startOfYear from 'date-fns/startOfYear';

import { getEndOfDay } from './getEndOfDay';
import { getLastQuartersDate } from './getLastQuartersDate';
import { getLastWeeksDate } from './getLastWeeksDate';
import { getLastYearsDate } from './getLastYearsDate';
import { handleRangeSelect } from './handleRangeSelect';
import { RangeChangeMeta } from './types';

const today = getEndOfDay();
const lastDayOfPreviousMonth = new Date(getEndOfDay().setDate(0));
const dayInPreviousWeek = getLastWeeksDate();
const dayInPreviousQuarter = getLastQuartersDate();
const dayInPreviousYear = getLastYearsDate();

export const ranges = [
  'ThisWeek',
  'LastWeek',
  'ThisMonth',
  'LastMonth',
  'ThisQuarter',
  'LastQuarter',
  'ThisYear',
  'LastYear',
] as const;

export type Range = (typeof ranges)[number];

export const getRangeByName = (range: Range) => {
  switch (range) {
    case 'ThisWeek':
      return {
        from: startOfWeek(today),
        to: endOfWeek(today),
      };
    case 'LastWeek':
      return {
        from: startOfWeek(dayInPreviousWeek),
        to: endOfWeek(dayInPreviousWeek),
      };
    case 'ThisMonth':
      return { from: startOfMonth(today), to: endOfMonth(today) };
    case 'LastMonth':
      return {
        from: startOfMonth(lastDayOfPreviousMonth),
        to: lastDayOfPreviousMonth,
      };
    case 'ThisQuarter':
      return {
        from: startOfQuarter(today),
        to: endOfQuarter(today),
      };
    case 'LastQuarter':
      return {
        from: startOfQuarter(dayInPreviousQuarter),
        to: endOfQuarter(dayInPreviousQuarter),
      };
    case 'ThisYear':
      return {
        from: startOfYear(today),
        to: endOfYear(today),
      };
    case 'LastYear':
      return {
        from: startOfYear(dayInPreviousYear),
        to: endOfYear(dayInPreviousYear),
      };
  }
};

export const selectCustomRangeHandler = (range: Range, meta: RangeChangeMeta) => {
  const { setActiveRange, setSelectedMonth } = meta;

  if (ranges.includes(range)) {
    setActiveRange(range);
  }

  switch (range) {
    case 'ThisWeek':
      setSelectedMonth(endOfWeek(today));

      return handleRangeSelect(getRangeByName(range), meta);
    case 'LastWeek':
      setSelectedMonth(endOfWeek(dayInPreviousWeek));

      return handleRangeSelect(getRangeByName(range), meta);
    case 'ThisMonth':
      setSelectedMonth(endOfMonth(today));

      return handleRangeSelect({ from: startOfMonth(today), to: endOfMonth(today) }, meta);
    case 'LastMonth':
      setSelectedMonth(lastDayOfPreviousMonth);

      return handleRangeSelect(getRangeByName(range), meta);
    case 'ThisQuarter':
      setSelectedMonth(endOfQuarter(today));

      return handleRangeSelect(getRangeByName(range), meta);
    case 'LastQuarter':
      setSelectedMonth(endOfQuarter(dayInPreviousQuarter));

      return handleRangeSelect(getRangeByName(range), meta);
    case 'ThisYear':
      setSelectedMonth(endOfYear(today));

      return handleRangeSelect(getRangeByName(range), meta);
    case 'LastYear':
      setSelectedMonth(endOfYear(dayInPreviousYear));

      return handleRangeSelect(getRangeByName(range), meta);
  }

  return null;
};
